import React, { useEffect, useLayoutEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  copyMenuItemWithoutSubItems,
  copyMenuItemWithoutParent,
  addToppingToQuestion,
  addMenuItemToQuestion,
  removeToppingToQuestion,
  calcSubTotal,
  findQuestionOptions,
  toggleIncludedItem,
  getQuickAddProperties,
  dereffItems,
  checkIfPopUpRequired,
  useQuickAddProperties,
  groupBy
} from '../../../helpers/basketHelpers';
import { customItemOptionsToGtmParams } from '../../../helpers/gtmEventHelpers';
import CustomiseCard from './CustomiseCard';
import chevronUp from '../../../img/icons/ChevronUpThin.svg'
import { RequiredTag } from '../../../components/RequiredTag';
import { gtmEvent } from '../../../helpers/commonHelpers';
import { useConfig } from '../../../helpers/useConfig';

const getNextSection = (result, item, isDuoRomana, selectHalfQuestionId) => {
  const newOptions = findQuestionOptions(result, item.name, !isDuoRomana, selectHalfQuestionId);
  const itemSelectedOptions = item.subProducts.filter(el => newOptions.find(option => el.id === option.id));

  const newNextItems = itemSelectedOptions.reduce((acc, cur) => acc.concat(cur.subProducts), []);
  return newNextItems
}

const checkCanAddForFree = (subProducts) => {
  if (!subProducts) return false;
  const groupedItems = groupBy(subProducts, 'isIncluded');
  const includedTotal = groupedItems[true]?.length ?? 0;
  const excludedTotal = groupedItems[false]?.reduce((acc, cur) => acc + cur.quantity, 0) ?? 0
  return includedTotal > excludedTotal;
}

const CustomiseSection = ({
  item,
  NewComponent,
  result,
  setCustomisedObj,
  invalidSections,
  setInvalidSections,
  showErrors,
  resetItem,
  outOfStockItems,
  duoRomanaIds,
  isDuoRomana,
  handleRequiredScrolling,
  currentTier,
  loyaltySchemeVersion,
  showCalories,
  customId,
  selectedOptions,
  onSelectedOptionsChange,
  isPizza,
  quantity,
  handleBaseNameClick,
  canExpand = true,
  handleExpandError,
  clearOptionsForSection,
  setNotification,
  selectHalfQuestionId,
  parentSelectedOptionItemId,
  setChoiceModalState
}) => {
  const itemLowerCaseName = item.name?.toLowerCase().trim();
  const selectedOptionItemId = `${parentSelectedOptionItemId ? parentSelectedOptionItemId + '.' : ''}${itemLowerCaseName}`;
  const config = useConfig();
  const brand = config?.BRAND;
  const inApp = useSelector(state => state.session.inApp);
  const isDelivery = useSelector(state => state.basket.isDelivery);
  const basket = useSelector(state => state.basket);
  const [nextItems, setNextItems] = useState(getNextSection(result, item, isDuoRomana, selectHalfQuestionId));
  const [error, setError] = useState(!!invalidSections.find(el => el === selectedOptionItemId));
  const [showSectionValidation, setShowSectionValidation] = useState(selectedOptions[selectedOptionItemId] ? true : false);
  const { filters } = useSelector(state => state.menu);
  const required = item.min === 1 && item.max <= item.min;
  const { isTabletScreen, customer } = useSelector(state => state.session);
  const baseSelections = useSelector(state => state.menu.baseSelections);
  const popupModalIds = useSelector(state => state.session.frontEndConfig?.popupModalIds);
  const { popupRequired } = useQuickAddProperties(item);
  const showSection = item && item.subProducts.length !== 1 && item.subProducts[0]?.name !== 'Standard' && (isDuoRomana && item.name !== 'Choose your pepper' || !isDuoRomana);
  const [isExpanded, setIsExpanded] = useState(
    (canExpand && !isDuoRomana && itemLowerCaseName != 'customise your pizza') || // the new customise your pizza section + duo romana starts closed, else expand like normal
    (customId) || // Always start expanded for customised items
    (isDuoRomana && selectedOptions[selectedOptionItemId]?.length > 0) // Keep expanded if the section already has a selection on duo romana
  );

  const isRadio = (item.min === 1 && item.max <= item.min);
  const canAddForFree = useMemo(() => checkCanAddForFree(selectedOptions[selectedOptionItemId], [selectedOptions[selectedOptionItemId]]));
  if (itemLowerCaseName.includes("select") && itemLowerCaseName.includes("half")) {
    selectHalfQuestionId = item.id;
  }
  const sectionsWithResetButton = ['extra toppings', 'customise your pizza', 'customise this half'];
  const sectionsOnBothHalves = ['customise this half', 'choose your pepper'];

  const canChildExpand = showSection ?
    !!selectedOptions[selectedOptionItemId]?.length || !isDuoRomana :
    !!selectedOptions[parentSelectedOptionItemId]?.length || !isDuoRomana;

  const [selectedQuickAddOption, setSelectedQuickAddOption] = useState();

  useLayoutEffect(() => {
    const invalid = invalidSections.find(el => el === selectedOptionItemId);
    if (invalid) {
      setError(true);
    } else {
      setError(false);
    }
  }, [invalidSections]);

  useLayoutEffect(() => {
    return () => {
      if (checkIfPopUpRequired(item, popupModalIds)) return;  // Don't clear the selected section if it's a pepper else we lose the choice modal edit selection)
      const newOptions = findQuestionOptions(result, item.name, !isDuoRomana, selectHalfQuestionId);
      onSelectedOptionsChange(newOptions,selectedOptionItemId);
    }
  }, []);

  useEffect(() => {
    const newOptions = findQuestionOptions(result, item.name, !sectionsOnBothHalves.includes(item.name?.toLowerCase()) || !isDuoRomana, selectHalfQuestionId);
    const itemSelectedOptions = item.subProducts.filter(el => newOptions.find(option => el.id === option.id));
    if (itemSelectedOptions?.length > 0 && itemSelectedOptions[0]?.id === baseSelections.find(b => b.product === item.id)?.base ||
      (customId && !newOptions[0]?.isPlaceholder) ||
      (itemSelectedOptions[0]?.id === '111' || itemSelectedOptions[0]?.id === '27')) {
      // Only select an option if the base dropdown has been populated or if it's a custom item (avoids any quick add selections)
      onSelectedOptionsChange(newOptions, selectedOptionItemId);
    }

    const newNextItems = itemSelectedOptions.reduce((acc, cur) => acc.concat(cur.subProducts), []);

    // Don't show extra toppings section for the old duo romana 
    if (isDuoRomana && newNextItems.find(n => n.name === 'Extra toppings')) return;

    setNextItems(newNextItems);
  }, [result]);

  useEffect(() => {
    if (required && showSection) {
      const hasSelections = selectedOptions[selectedOptionItemId] && selectedOptions[selectedOptionItemId]?.length !== 0;

      setInvalidSections(prevInvalidSections => {
        return hasSelections
          ? prevInvalidSections.filter(el => el !== selectedOptionItemId)
          : [...prevInvalidSections, selectedOptionItemId];
      });

      setShowSectionValidation(!!hasSelections);
    }
  }, [selectedOptions[selectedOptionItemId]]);

  const findAndReplace = (resultObj, option, replaceMethod, foundSelectHalfQuestionId = false, selectedOptionItemIdOverride, shouldLog = false) => {
    const newValue = { ...resultObj };
    if (!foundSelectHalfQuestionId) {
      foundSelectHalfQuestionId = !isDuoRomana ||
        !selectHalfQuestionId ||  // Needed for old duo romana (pre customisable toppings)
        (selectHalfQuestionId && (option.parent?.id === selectHalfQuestionId || newValue.id === selectHalfQuestionId));
    }

    if (newValue.id === option.parent?.parent?.id && foundSelectHalfQuestionId) {
      let question = newValue.subProducts.find(p => p.id === option.parent.id);
      if (!question) {
        question = { ...option.parent, subProducts: [], calcSubTotal, quantity: 1 };
        newValue.subProducts.push(copyMenuItemWithoutParent(question));
      }
      if (question.quantity = 0) question.quantity = 1;
      replaceMethod(item, question, option);
      onSelectedOptionsChange(question.subProducts, selectedOptionItemIdOverride ?? selectedOptionItemId);
      setNextItems(option.subProducts.map(p => ({ ...p, parent: copyMenuItemWithoutSubItems(question) })));
    }
    else if (newValue.id === option.parent.id && foundSelectHalfQuestionId) {
      replaceMethod(item, newValue, option);
      onSelectedOptionsChange(newValue, selectedOptionItemIdOverride ?? selectedOptionItemId);
      setNextItems(option.subProducts);
    }
    else {
      newValue.subProducts.map(product => findAndReplace(product, option, replaceMethod, foundSelectHalfQuestionId, selectedOptionItemIdOverride, shouldLog));
    }
    return newValue;
  };

  const handleCustomisation = (customisedObj, option) => {
    const event = {
      channel: inApp ? 'App' : 'Web',
      currency: brand === 'PE' ? 'GBP' : 'EUR',
      value: basket.total,
      clickAndCollectEvent: isDelivery
        ? 'Delivery_Product_Details_Clicked'
        : 'Collection_Product_Details_Clicked',
    };

    const item = {
      item_id: customisedObj.id,
      item_name: customisedObj.name,
      price: customisedObj.calcSubTotal(),
      quantity: quantity,
      item_brand: brand,
      item_category: customisedObj.categoryName
    };

    if (customer?.customerSessionToken?.pizzaExpressId) {
      event.customerId = customer.customerSessionToken.pizzaExpressId;
    }

    customItemOptionsToGtmParams(result, item);

    event['items'] = [item];

    gtmEvent('product_details_event', event);
    setCustomisedObj(customisedObj);
    if (required) handleRequiredScrolling(selectedOptionItemId);
  };

  const handleSelectCard = async (option) => {
    try {
      const newOption = dereffItems(option);

      let choiceId = false;
      const { popupRequired } = getQuickAddProperties(newOption, popupModalIds) || {};
      // Handle popup question if required
      if (popupRequired && isDuoRomana) {
        choiceId = await getChoiceModalResult(popupRequired);
        if (!choiceId) return; // Early exit if no choice is made
      }
      const customisedObj = findAndReplace(result, newOption, addMenuItemToQuestion);
      handleCustomisation(customisedObj, newOption);
      if (choiceId) {
        const choice = newOption.subProducts[0].subProducts[0].subProducts[0].subProducts.find(sp => sp.id === choiceId);
        const customisedObjWithPepperChoice = findAndReplace(result, choice, addMenuItemToQuestion, false, `${selectedOptionItemId}.please choose.choose your pepper`);
        handleCustomisation(customisedObjWithPepperChoice, choice);
      }
    } catch (error) {
    }
  };

  const handleSelectTopping = (option) => {
    let customisedObj = findAndReplace(result, option, addToppingToQuestion);
    handleCustomisation(customisedObj, option);
    setNotification('Topping added')
  };

  const handleUnselectTopping = (option) => {
    let customisedObj = findAndReplace(result, option, removeToppingToQuestion);
    handleCustomisation(customisedObj, option);
    setNotification("Topping removed")
  };

  const handleIncludedItemToggle = (option, isAdding) => {
    let customisedObj = findAndReplace(result, option, toggleIncludedItem);
    handleCustomisation(customisedObj, option);
    setNotification(isAdding ? 'Topping added' : 'Topping removed')
  }

  const handleAccordion = () => {
    if (!canExpand) handleExpandError();
    else setIsExpanded(!isExpanded);
  };

  const handleChoiceModalEdit = async (e) => {
    e?.stopPropagation();
    const choiceId = await getChoiceModalResult(popupRequired, selectedQuickAddOption.selected.id);
    if (!choiceId) return; // Early exit if no choice is made
    const choice = item.subProducts.find(sp => sp.id === selectedOptions[selectedOptionItemId][0].id).subProducts[0].subProducts[0].subProducts[0].subProducts.find(sp => sp.id === choiceId);
    const customisedObjWithPepperChoice = findAndReplace(result, choice, addMenuItemToQuestion, false, `${selectedOptionItemId}.please choose.choose your pepper`, true);
    handleCustomisation(customisedObjWithPepperChoice, choice);
  }

  const getChoiceModalResult = async (question, prePopulatedChoiceId) => {
    try {
      return await new Promise((resolve, reject) => {
        setChoiceModalState({
          resolve,
          reject,
          choices: question.subProducts,
          question,
          prePopulatedChoiceId
        });
      });
    } catch (err) {
      return false;
    }
  };

  const handleExpandErrorFn = () => {
    if (!showSection) {
      handleExpandError();  // Pass the error up to the parent
    }
    else {
      setError(true);
      setShowSectionValidation(true);
      handleRequiredScrolling();
    }
  }

  const handleResetItem = () => {
    if (isPizza) handleAccordion();
    resetItem();
  }

  return (
    <>
      {
        item &&
        <>
          {
            showSection ? (
              <div className='customise-section' data-section-error-id={error ? selectedOptionItemId : null}>
                <div className='customise-section-title-container'>
                  <div className='customise-section-header-container'>
                    <h2 className="customise-section-header">{item.name}</h2>
                    {
                      required ?
                        <RequiredTag isErrored={error} showValidation={showErrors ? true : showSectionValidation} />
                        :
                        <img
                          alt="null"
                          src={chevronUp}
                          aria-label='chevron'
                          style={{ transform: !isExpanded ? 'rotate(180deg)' : '', cursor: 'pointer' }}
                          onClick={() => { handleAccordion() }}
                        />
                    }
                  </div>
                  {
                    required ?
                      <p className='customise-section-subheader'>{!isDuoRomana ? 'Select one of the options below.' : 'Choose one of the options below.'}</p>
                      :
                      <p className='customise-section-subheader'>Add or remove toppings. Each additional topping is chargeable.</p>
                  }
                  {
                    item.subProducts.some(sp => sp.isIncluded) && isExpanded &&
                    <p className='customise-section-sub-subheader'>We cannot guarantee that our dishes are 100% free from allergens or contaminants or that nutritional values are as stated, with the exception of gluten-free dishes which describes food that contain gluten at levels of no more than 20 parts per million.</p>
                  }
                  {
                    !isTabletScreen && isExpanded && (sectionsWithResetButton.includes(item.name?.toLowerCase().trim())) &&
                    <a role="button" className='link dm-sans' style={{ fontSize: 18 }} onClick={handleResetItem}>Reset {isPizza ? 'Pizza' : 'Toppings'}</a>
                  }
                </div>
                {
                  isTabletScreen && (sectionsWithResetButton.includes(item.name?.toLowerCase().trim())) &&
                  <a role="button" className='link dm-sans customise-section-reset' style={{ fontSize: 18 }} onClick={handleResetItem}>Reset {isPizza ? 'Pizza' : 'Toppings'}</a>
                }
                {(isRadio || isExpanded) &&
                  <>
                    <div className='customise-card-option-container'>
                      {
                        item.subProducts.some(sp => sp.isIncluded) &&
                        <>
                          <div className='customise-card-topping-header'>Included toppings</div>
                          {item.subProducts?.filter(option => option.isIncluded)
                            .filter(el => {
                              if (!filters.length) return true;
                              if (selectedOptions[selectedOptionItemId]?.find(o => o.name === el.name)) return true; // If the subproduct is already selected, don't filter it out
                              if (!el.dietPreferences.length) return true; // If the subproduct has no diet preferences, assume it is a "product choice item" (e.g. "Choose your base" & "Why not add dough sticks")
                              if (filters.every(pref => el.dietPreferences.includes(pref))) return true;
                            })
                            .map((option, i) => {
                              const selected = selectedOptions[selectedOptionItemId]?.find(o => o.id === option.id);
                              return (
                                <CustomiseCard
                                  index={i}
                                  name={option.name}
                                  cost={option.cost}
                                  parent={item}
                                  option={option}
                                  selected={selected}
                                  selectedQuickAddOption={selectedQuickAddOption}
                                  setSelectedQuickAddOption={setSelectedQuickAddOption}
                                  handleChoiceModalEdit={handleChoiceModalEdit}
                                  cardSelect={() => handleSelectCard(option)}
                                  handleIncrease={() => handleSelectTopping(option)}
                                  handleDecrease={() => handleUnselectTopping(option)}
                                  handleIncludedToggle={(isAdding) => handleIncludedItemToggle(option, isAdding)}
                                  reachedMax={item.max <= selectedOptions[selectedOptionItemId]?.reduce((acc, option) => option.quantity + acc, 0)}
                                  required={required}
                                  currentTier={currentTier}
                                  loyaltySchemeVersion={loyaltySchemeVersion}
                                  showCalories={showCalories}
                                  key={option.id}
                                  setNotification={setNotification}
                                  isRadio={isRadio}
                                  isDuoRomana={isDuoRomana}
                                  handleBaseNameClick={handleBaseNameClick}
                                  selectedOptions={selectedOptions}
                                  selectedOptionItemId={selectedOptionItemId}
                                />
                              );
                            })}
                        </>
                      }
                      {
                        item.subProducts?.some(sp => sp.isIncluded) &&
                        <div className='customise-card-topping-header'>Additional toppings</div>
                      }
                      {
                        item.subProducts?.filter(option => !option.isIncluded)
                          .filter(el => {
                            if (!filters.length) return true;
                            if (selectedOptions[selectedOptionItemId]?.find(o => o.name === el.name)) return true; // If the subproduct is already selected, don't filter it out
                            if (!el.dietPreferences.length) return true; // If the subproduct has no diet preferences, assume it is a "product choice item" (e.g. "Choose your base" & "Why not add dough sticks")
                            if (filters.every(pref => el.dietPreferences.includes(pref))) return true;
                          })
                          .map((option, i) => {
                            const selected = selectedOptions[selectedOptionItemId]?.find(o => o.id === option.id);
                            return (
                              <CustomiseCard
                                index={i}
                                name={option.name}
                                cost={option.cost}
                                parent={item}
                                option={option}
                                selected={selected}
                                selectedQuickAddOption={selectedQuickAddOption}
                                setSelectedQuickAddOption={setSelectedQuickAddOption}
                                handleChoiceModalEdit={handleChoiceModalEdit}
                                cardSelect={() => handleSelectCard(option)}
                                handleIncrease={() => handleSelectTopping(option)}
                                handleDecrease={(isAdding) => handleUnselectTopping(option, isAdding)}
                                reachedMax={item.max <= selectedOptions[selectedOptionItemId]?.reduce((acc, option) => option.quantity + acc, 0)}
                                required={required}
                                currentTier={currentTier}
                                loyaltySchemeVersion={loyaltySchemeVersion}
                                showCalories={showCalories}
                                key={option.id}
                                canAddForFree={canAddForFree}
                                setNotification={setNotification}
                                isRadio={isRadio}
                                isDuoRomana={isDuoRomana}
                                handleBaseNameClick={handleBaseNameClick}
                                selectedOptions={selectedOptions}
                                selectedOptionItemId={selectedOptionItemId}
                                clearOptionsForSection={clearOptionsForSection}
                              />
                            );
                          })
                      }
                    </div>
                  </>
                }
              </div>
            ) : null
          }
          {
            nextItems?.map((childQuestion, idx) =>
              <NewComponent
                key={`${item.id}-${idx}`}
                item={childQuestion}
                NewComponent={NewComponent}
                result={result}
                setCustomisedObj={setCustomisedObj}
                invalidSections={invalidSections}
                setInvalidSections={setInvalidSections}
                showErrors={showErrors}
                resetItem={resetItem}
                outOfStockItems={outOfStockItems}
                isDuoRomana={isDuoRomana}
                duoRomanaIds={duoRomanaIds}
                handleRequiredScrolling={handleRequiredScrolling}
                showCalories={showCalories}
                customId={customId}
                selectedOptions={selectedOptions}
                onSelectedOptionsChange={onSelectedOptionsChange}
                isPizza={isPizza}
                quantity={quantity}
                handleBaseNameClick={handleBaseNameClick}
                canExpand={canChildExpand}
                handleExpandError={handleExpandErrorFn}
                clearOptionsForSection={clearOptionsForSection}
                setNotification={setNotification}
                selectHalfQuestionId={selectHalfQuestionId}
                parentSelectedOptionItemId={selectedOptionItemId}
                setChoiceModalState={setChoiceModalState}
              />
            )
          }
        </>
      }
    </>
  );
};

export default CustomiseSection;
